import {
  addUtcDays,
  daysBetweenYmd,
  formatCount,
  formatPercent,
  formatPercentOneDecimal,
  pluraliseCount,
  ratioPercent,
  shiftYmd,
  todayYmdInZone,
  formatUkShortDateTime,
  formatUkTime,
} from '../lib/calc';

// Re-exported so existing import sites keep working — the maths now lives in
// `lib/calc/dates`, this module keeps only the orders-flavoured wrappers below.
export { addUtcDays, shiftYmd };
import type {
  CollectionCondition,
  CollectionSource,
  CollectionStage,
  CollectionStageProvenance,
  OperationalMetric,
  OrderLegDirection,
  OrdersDailyRow,
  OrdersDailyStatusBucket,
} from '../types/ordersDailyTruth';

/** GET /orders/v1/daily rejects ranges longer than this. */
export const ORDERS_DAILY_MAX_RANGE_DAYS = 93;

export function clampOrdersDailyRange(
  startDate: string,
  endDate: string,
): { startDate: string; endDate: string; clamped: boolean } {
  if (startDate > endDate) {
    return clampOrdersDailyRange(endDate, startDate);
  }
  const days = (daysBetweenYmd(startDate, endDate) ?? 0) + 1;
  if (days <= ORDERS_DAILY_MAX_RANGE_DAYS) {
    return { startDate, endDate, clamped: false };
  }
  return {
    startDate: addUtcDays(endDate, -(ORDERS_DAILY_MAX_RANGE_DAYS - 1)),
    endDate,
    clamped: true,
  };
}

/**
 * Share of a bucket within its denominator, for the "% of today's work in this
 * state" caption on the status cards. Consignment-based, matching completion.
 *
 * This is NOT how completion is calculated — `summary.deliveries.completion_pct`
 * comes from the truth layer and must be displayed as given. Deriving a
 * completion figure here would let the dashboard and Zoe disagree.
 */
export function bucketSharePct(bucket: OperationalMetric, total: OperationalMetric): number | null {
  return ratioPercent(bucket.consignments, total.consignments);
}

/** Lift volume — whole numbers clean, fractional pallets to one decimal. */
export function formatLifts(value: number): string {
  return formatCount(value);
}

/** Plural-aware consignment count, e.g. "1 consignment" / "17 consignments". */
export function formatConsignments(value: number): string {
  return pluraliseCount(value, 'consignment');
}

/**
 * Whole-number percent for a gauge / caption. Milestone-guarded so 238/239
 * delivered reads "99%", not "100%" — see `lib/calc/percentages`.
 */
export function formatPct(value: number | null): string {
  return formatPercent(value);
}

/** One decimal, same milestone guard: 184 of 188 reads "97.9%". For the headline cards. */
export function formatPctOneDecimal(value: number | null): string {
  return formatPercentOneDecimal(value);
}

/** Wallboard clock for "Data current as of HH:mm:ss". */
export function formatDataCurrentAs(iso: string | null): string | null {
  if (!iso) return null;
  const t = formatUkTime(iso, { seconds: true }, '');
  return t || null;
}

export const DELIVERY_BUCKET_LABELS: Record<OrdersDailyStatusBucket, string> = {
  delivered: 'Delivered',
  partial: 'Part-delivered',
  out_for_delivery: 'Out for delivery',
  at_depot: 'At depot, due today',
  in_transit: 'In transit',
  removed_from_plan: 'Removed from plan',
  exception: 'Departed hub, not received',
  unmapped: 'Unmapped',
};

export const DELIVERY_BUCKET_HINTS: Record<OrdersDailyStatusBucket, string> = {
  delivered: 'POD received',
  partial: 'Some pallets delivered, rest outstanding',
  out_for_delivery: 'On vehicle now',
  at_depot: 'Not yet dispatched',
  in_transit: 'At a Palletways hub, not yet at the delivery depot',
  removed_from_plan: 'Taken off the delivery vehicle by the depot. Usually re-booked to a later day, sometimes sent out again today',
  exception: 'Chase the collecting depot',
  unmapped: 'Counted, not dropped',
};

/**
 * A collection's own lifecycle (`collection_stage`), in order. Collections do
 * not reuse the delivery chips: once trunked, where the consignment goes next
 * is the delivering depot's progress, not the collection's.
 */
export const COLLECTION_STAGE_ORDER: CollectionStage[] = [
  'awaiting_collection',
  'collected',
  'at_collection_depot',
  'dispatched_onward',
];

export const COLLECTION_STAGE_LABELS: Record<CollectionStage, string> = {
  awaiting_collection: 'Awaiting collection',
  collected: 'Collected',
  at_collection_depot: 'At depot',
  dispatched_onward: 'Dispatched onward',
};

export const COLLECTION_STAGE_HINTS: Record<CollectionStage, string> = {
  awaiting_collection: 'No collection evidence yet: normal until the collection window closes',
  collected: 'POC recorded, not yet scanned at the depot',
  at_collection_depot: 'Bay-checked at the collecting depot, not yet trunked',
  dispatched_onward: 'Scanned onto the trunk, or seen further down the network',
};

/** Conditions that need someone's attention, in the order the page shows them. */
export const COLLECTION_ATTENTION_CONDITIONS: Exclude<CollectionCondition, 'on_track'>[] = [
  'needs_investigation',
  'overdue',
  'at_risk',
  'expected_exception',
];

export const COLLECTION_CONDITION_LABELS: Record<CollectionCondition, string> = {
  on_track: 'On track',
  at_risk: 'At risk',
  overdue: 'Overdue',
  needs_investigation: 'Needs investigation',
  expected_exception: 'Expected exception',
};

export const COLLECTION_CONDITION_HINTS: Record<CollectionCondition, string> = {
  on_track: 'Nothing to chase',
  at_risk: 'Still awaiting collection late on its collection day',
  overdue: 'Still awaiting collection after its collection day',
  needs_investigation: 'Marked collected but never scanned in at the depot. Check it was really collected, or that the scan reached us',
  expected_exception: 'Would need chasing, but this customer is on the known-exceptions list',
};

export const COLLECTION_SOURCE_LABELS: Record<CollectionSource, string> = {
  own_customer: 'Own customer',
  network: 'Network',
  unknown: 'Booking not yet read',
};

/**
 * Provenance worth calling out on a collection stage (§13.2 terms). `Observed`
 * is the normal case and `Unknown` is what "awaiting" already says, so neither
 * gets a tag.
 */
export function collectionProvenanceNote(
  provenance: CollectionStageProvenance | undefined,
): { label: string; title: string } | null {
  if (provenance === 'Inferred') {
    return { label: 'Inferred', title: 'Not scanned at this stage. A later scan shows it must have happened' };
  }
  if (provenance === 'Asserted') {
    return { label: 'Asserted', title: "Only Palletways' job status says it is done. No scan or POC on record" };
  }
  return null;
}

export type OrdersDailyKpiFilter =
  | { kind: 'all' }
  | { kind: 'collections' }
  | { kind: 'deliveries' }
  | { kind: 'eta' }
  | { kind: 'eta_late' }
  | { kind: 'tap_issues' }
  | { kind: 'open' }
  | { kind: 'collected' }
  | { kind: 'delivered_complete' }
  | { kind: 'completion' }
  | { kind: 'bucket'; bucket: OrdersDailyStatusBucket; direction: OrderLegDirection }
  | { kind: 'collection_stage'; stage: CollectionStage }
  | { kind: 'collection_without_poc' }
  | { kind: 'collection_not_received' }
  | { kind: 'collection_condition'; condition: CollectionCondition }
  | { kind: 'collection_source'; source: CollectionSource }
  | { kind: 'missed_am' }
  | { kind: 'at_risk_1700' }
  | { kind: 'booked_in_excluded' }
  | { kind: 'cancelled' };

/** Same predicates as the headline cards — never re-bucket in the UI. */
export function matchesOrdersDailyKpi(row: OrdersDailyRow, filter: OrdersDailyKpiFilter): boolean {
  // A cancelled leg is in no figure, so it matches no figure's filter.
  if (row.cancelled) return filter.kind === 'all' || filter.kind === 'cancelled';
  switch (filter.kind) {
    case 'cancelled':
      return false;
    case 'all':
      return true;
    case 'collections':
      return row.direction === 'collection';
    case 'deliveries':
      return row.direction === 'delivery';
    case 'eta':
      return row.direction === 'delivery' && row.eta_on_time !== null && row.eta_on_time !== undefined;
    case 'tap_issues':
      return row.direction === 'delivery' && row.delivered && (row.gps_tap_flags?.length ?? 0) > 0;
    case 'eta_late':
      return row.direction === 'delivery' && row.eta_on_time === false;
    case 'open':
      return row.open;
    case 'collected':
      return row.direction === 'collection' && row.collected;
    case 'delivered_complete':
      return row.direction === 'delivery' && row.delivered;
    case 'completion':
      return (row.direction === 'collection' && row.collected)
        || (row.direction === 'delivery' && row.delivered);
    case 'bucket':
      return row.direction === filter.direction && row.status_bucket === filter.bucket;
    case 'collection_stage':
      return row.direction === 'collection' && row.collection_stage === filter.stage;
    case 'collection_without_poc':
      return row.direction === 'collection'
        && row.collection_stage !== undefined
        && row.collection_stage !== 'awaiting_collection'
        && row.collection_poc_recorded === false;
    case 'collection_not_received':
      return row.direction === 'collection' && row.collection_not_received === true;
    case 'collection_condition':
      return row.direction === 'collection' && row.collection_condition === filter.condition;
    case 'collection_source':
      return row.direction === 'collection' && (row.collection_source ?? 'unknown') === filter.source;
    case 'missed_am':
      return row.missed_am === true;
    case 'at_risk_1700':
      return row.at_risk_1700 === true;
    case 'booked_in_excluded':
      return row.booked_in_excluded === true;
    default:
      return true;
  }
}

export function directionLabel(direction: OrderLegDirection): string {
  return direction === 'collection' ? 'Collection' : 'Delivery';
}

/** DueDate + DueTime for the V4 table. Unparseable dates stay as stored. */
export function formatOrdersDue(dueDate?: string, dueTime?: string): string {
  if (!dueDate?.trim()) return '—';
  const iso = dueDate.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  const day = iso ? `${iso[3]}/${iso[2]}` : dueDate.trim();
  const time = dueTime?.trim().slice(0, 5);
  return time ? `${day} ${time}` : day;
}

/** PodDate in front of PodTime (same day/month style as Due). */
export function formatOrdersPod(podDate?: string, podTime?: string): string {
  const dateRaw = podDate?.trim();
  const time = podTime?.trim().slice(0, 5);
  if (!dateRaw && !time) return '—';
  let day = dateRaw || '';
  if (dateRaw) {
    const iso = dateRaw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    day = iso ? `${iso[3]}/${iso[2]}` : dateRaw;
  }
  if (day && time) return `${day} ${time}`;
  return day || time || '—';
}

/**
 * PDT vs POD on-time result for the drill-down table.
 * Null / undefined = not in the ETA% denominator (no PDT, not delivered, incomplete clocks).
 */
export function formatOrdersEtaOnTime(etaOnTime?: boolean | null): string {
  if (etaOnTime === true) return 'On time';
  if (etaOnTime === false) return 'Late';
  return '—';
}

/** Why a delivery has no GPS reading, short (cell) and in full (tooltip). Never left blank as if fine. */
const GPS_NO_READING: Record<string, { short: string; full: string }> = {
  ambiguous: { short: 'Unclear', full: 'GPS shows more than one possible visit near the arrival time' },
  not_at_site: { short: 'Not seen at site', full: 'The vehicle’s GPS did not stop at the delivery address around the arrival time' },
  gps_gap: { short: 'No signal', full: 'The vehicle’s GPS sent nothing around the arrival time' },
  no_telematics: { short: 'No tracker', full: 'The vehicle on the order has no Samsara GPS' },
  no_vehicle: { short: 'Not our vehicle', full: 'No vehicle of ours was on the order that day (network partner delivery)' },
};

/**
 * The driver's taps against the van's stop, in words (operational checks:
 * arrival / POD entered late, early, or away from the site). Empty when they
 * line up.
 */
export function describeTapIssues(row: Pick<OrdersDailyRow,
  'gps_tap_flags' | 'gps_tap_distance_from_site_m' | 'gps_completion_minus_departure_minutes'>): string[] {
  const flags = new Set(row.gps_tap_flags ?? []);
  const out: string[] = [];
  if (flags.has('no_arrival_tap')) out.push('No arrival tap');
  if (flags.has('arrival_tapped_late')) out.push('Arrival tapped after the vehicle got there');
  if (flags.has('arrival_tapped_early')) out.push('Arrival tapped before the vehicle got there');
  if (flags.has('completion_after_departure')) {
    const m = row.gps_completion_minus_departure_minutes;
    out.push(m != null && m > 0 ? `POD tapped ${formatGapMinutes(m)} after the vehicle left` : 'POD tapped after the vehicle left');
  }
  if (flags.has('completion_before_arrival')) out.push('POD tapped before the vehicle got there');
  if (flags.has('tap_away_from_site')) {
    const d = row.gps_tap_distance_from_site_m;
    out.push(d != null ? `Tapped ${d >= 1000 ? `${(d / 1000).toFixed(1)} km` : `${Math.round(d)} m`} from the address` : 'Tapped away from the address');
  }
  return out;
}

function formatGapMinutes(minutes: number): string {
  const m = Math.round(minutes);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  return m % 60 ? `${h} h ${m % 60} min` : `${h} h`;
}

export type GpsCheckTone = 'agrees' | 'disagrees' | 'reading' | 'none';

export interface GpsCheck {
  /** Cell headline: `Confirmed`, `GPS: on time` / `GPS: late` when it disagrees with Palletways, or why there is no reading. */
  headline: string;
  /** Second line: the van's GPS arrive / leave clock, when there is one. */
  detail?: string;
  tooltip: string;
  tone: GpsCheckTone;
  /** Driver taps out of step with the van's stop (describeTapIssues). */
  issues: string[];
}

/**
 * The GPS column: whether the van's GPS confirms the Palletways ETA verdict,
 * and what GPS says. The ETA verdict itself stays the Palletways one (the
 * driver's arrival tap); GPS is supporting evidence beside it. Null for rows
 * GPS is not read for (collections, deliveries not yet delivered).
 */
export function describeGpsCheck(row: Pick<OrdersDailyRow,
  'direction' | 'delivered' | 'eta_on_time' | 'gps_match_status' | 'gps_arrived_at' | 'gps_departed_at' | 'eta_gps_on_time' | 'gps_other_van'
  | 'gps_site_source' | 'gps_tap_flags' | 'gps_tap_distance_from_site_m' | 'gps_completion_minus_departure_minutes'>): GpsCheck | null {
  if (row.direction !== 'delivery' || !row.delivered) return null;
  const issues = describeTapIssues(row);
  if (!row.gps_match_status) {
    return { headline: 'Pending', tooltip: 'GPS not matched yet: matched after the arrival and confirmed overnight', tone: 'none', issues };
  }
  if (!row.gps_arrived_at) {
    const reason = GPS_NO_READING[row.gps_match_status];
    const notAtAddress = row.gps_match_status === 'not_at_site' && row.gps_site_source === 'booked_address';
    return {
      headline: notAtAddress ? 'Not at address' : reason?.short ?? row.gps_match_status,
      tooltip: notAtAddress
        ? 'The vehicle’s GPS never stopped at the delivery address that day, and no other vehicle of ours did'
        : reason?.full ?? `GPS: ${row.gps_match_status}`,
      tone: notAtAddress ? 'disagrees' : 'none',
      issues,
    };
  }
  const time = (v?: string) => (v ? v.slice(11, 16) : 'still there');
  const detail = `${time(row.gps_arrived_at)}–${time(row.gps_departed_at)}${row.gps_other_van ? ' · other vehicle' : ''}`;
  const clock = `Vehicle GPS arrived ${time(row.gps_arrived_at)}${row.gps_departed_at ? `, left ${time(row.gps_departed_at)}` : ', still on site'}`;
  const otherVan = row.gps_other_van ? ' (a different vehicle from the one scanned)' : '';
  if (row.gps_site_source === 'time_only') {
    return { headline: 'Vehicle stopped', detail, tooltip: `${clock}${otherVan}. No address to check it against: the vehicle stood still when the button was pressed, place not checked.`, tone: 'reading', issues };
  }
  if (row.gps_match_status === 'confirmed_elsewhere') {
    return { headline: 'Delivered elsewhere', detail, tooltip: `${clock}${otherVan}, where the driver tapped: the vehicle never stopped at the booked delivery address.`, tone: 'disagrees', issues };
  }
  if (row.gps_match_status === 'ambiguous') {
    return { headline: 'Unclear', detail, tooltip: `${clock}: more than one vehicle (or stop) at the address; the nearest to the driver’s tap is shown.`, tone: 'reading', issues };
  }
  const gps = row.eta_gps_on_time;
  if (gps == null) {
    return { headline: 'GPS visit', detail, tooltip: `${clock}${otherVan}. No ETA to judge it against.`, tone: 'reading', issues };
  }
  const gpsWord = gps ? 'on time' : 'late';
  if (row.eta_on_time == null || row.eta_on_time === gps) {
    return { headline: 'Confirmed', detail, tooltip: `${clock}${otherVan}: GPS also has it ${gpsWord}.`, tone: 'agrees', issues };
  }
  return {
    headline: `GPS: ${gpsWord}`,
    detail,
    tooltip: `${clock}${otherVan}: by GPS the vehicle was ${gpsWord}, while the Palletways arrival tap has it ${row.eta_on_time ? 'on time' : 'late'}.`,
    tone: 'disagrees',
    issues,
  };
}

const GPS_CHECK_ORDER: Record<GpsCheckTone, number> = { disagrees: 0, agrees: 1, reading: 2, none: 3 };

function gpsCheckSortValue(row: OrdersDailyRow): number {
  const c = describeGpsCheck(row);
  return c ? GPS_CHECK_ORDER[c.tone] : 4;
}

/** PDT / window local wall instant `YYYY-MM-DDTHH:MM:SS` → `DD/MM HH:MM`. */
export function formatOrdersLocalWallInstant(instant?: string | null): string {
  if (!instant?.trim()) return '—';
  return formatUkShortDateTime(instant, instant.trim());
}

export function formatOrdersService(row: Pick<OrdersDailyRow, 'service_code' | 'service_name'>): string {
  const code = row.service_code?.trim();
  const name = row.service_name?.trim();
  if (code && name) return `${code} · ${name}`;
  return code || name || '—';
}

export function formatGbp(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  const abs = Math.abs(value);
  const formatted = abs.toLocaleString(undefined, {
    minimumFractionDigits: abs % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });
  return value < 0 ? `−£${formatted}` : `£${formatted}`;
}

/** Missed fee (negative) or at-risk band for the table £ impact column. */
export function formatOrdersImpact(row: OrdersDailyRow): string {
  if (row.booked_in_excluded) return '—';
  if (row.missed_am && row.impact_gbp != null) return formatGbp(-row.impact_gbp);
  if (row.at_risk_1700) {
    const min = row.impact_gbp_min;
    const max = row.impact_gbp_max;
    if (min != null && max != null && min !== max) {
      return `${formatGbp(min)}–${formatGbp(max)} at risk`;
    }
    if (min != null) return `${formatGbp(min)} at risk`;
    if (max != null) return `${formatGbp(max)} at risk`;
    return 'At risk';
  }
  return '—';
}

export function formatServiceRiskMissedHeadline(impactGbp: number | null, consignments: number): string {
  if (consignments === 0) return '£0';
  if (impactGbp == null) return 'Fee unset';
  return formatGbp(-impactGbp);
}

export function formatServiceRiskAtRiskHeadline(
  min: number | null,
  max: number | null,
  consignments: number,
): string {
  if (consignments === 0) return '£0';
  if (min == null && max == null) return 'Rate unset';
  if (min != null && max != null && min !== max) {
    return `${formatGbp(min)} – ${formatGbp(max)}`;
  }
  return formatGbp(min ?? max);
}

/** Europe/London calendar day — the zone every orders cut-off (§2.5) is stated in. */
export function londonDateString(asOf: Date = new Date()): string {
  return todayYmdInZone('Europe/London', asOf);
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function ymdParts(ymd: string): { y: number; m: number; d: number } {
  const [y, m, d] = ymd.slice(0, 10).split('-').map(Number);
  return { y, m, d };
}

/** First calendar day of the month that contains `ymd`. */
export function startOfMonthYmd(ymd: string): string {
  const { y, m } = ymdParts(ymd);
  return `${y}-${pad2(m)}-01`;
}

/** Inclusive London-calendar range for Month to date (1st → today). */
export function londonMonthToDateRange(asOf: Date = new Date()): { start: string; end: string } {
  const today = londonDateString(asOf);
  return { start: startOfMonthYmd(today), end: today };
}

/** Inclusive London-calendar range for the previous full calendar month. */
export function londonLastMonthRange(asOf: Date = new Date()): { start: string; end: string } {
  const today = londonDateString(asOf);
  const { y, m } = ymdParts(today);
  const prevY = m === 1 ? y - 1 : y;
  const prevM = m === 1 ? 12 : m - 1;
  const lastDay = new Date(Date.UTC(prevY, prevM, 0)).getUTCDate();
  return {
    start: `${prevY}-${pad2(prevM)}-01`,
    end: `${prevY}-${pad2(prevM)}-${pad2(lastDay)}`,
  };
}

/** Whole calendar days from `ymd` to London today. Negative = still in the future. */
export function daysSinceYmd(ymd: string, asOf: Date = new Date()): number | null {
  if (!/^\d{4}-\d{2}-\d{2}/.test(ymd)) return null;
  return daysBetweenYmd(ymd, londonDateString(asOf));
}

/** "due today" / "1 day overdue" / "3 days overdue" from a delivery grain date. */
export function overdueLabel(ymd: string, asOf: Date = new Date()): string | null {
  const days = daysSinceYmd(ymd, asOf);
  if (days == null) return null;
  if (days <= 0) return 'due today';
  return `${days} day${days === 1 ? '' : 's'} overdue`;
}

export function isOrdersDailyLondonTodayRange(
  startDate: string,
  endDate: string,
  asOf: Date = new Date(),
): boolean {
  const today = londonDateString(asOf);
  return startDate === endDate && startDate === today;
}

/** At-risk (§2.5) only flags grain_date = London today — false for Yesterday-only ranges. */
export function ordersDailyRangeIncludesLondonToday(
  startDate: string,
  endDate: string,
  asOf: Date = new Date(),
): boolean {
  const today = londonDateString(asOf);
  return startDate <= today && endDate >= today;
}

export function serviceRiskSectionTitle(startDate: string, endDate: string, asOf: Date = new Date()): string {
  if (isOrdersDailyLondonTodayRange(startDate, endDate, asOf)) {
    return 'Service risk — what it is costing today';
  }
  return 'Service risk — in this period';
}

/** Copy for the three §2.5 cards — period-aware so Yesterday does not read as broken. */
export function serviceRiskMissedHint(opts: {
  consignments: number;
  impactGbp: number | null;
  missedCodesLabel: string;
  viewingLondonToday: boolean;
}): string {
  if (opts.impactGbp != null) {
    return opts.viewingLondonToday
      ? 'AM fee credited back (from your fee settings)'
      : 'Still without POD — live count (drops if POD arrives later)';
  }
  if (opts.consignments > 0) {
    return `Counts ready — set fee_by_code for ${opts.missedCodesLabel} in orders KPI config for £`;
  }
  return opts.viewingLondonToday
    ? 'No missed AM promises today'
    : 'No AM promises still without POD in this period';
}

export function serviceRiskAtRiskHint(opts: {
  consignments: number;
  impactGbpMin: number | null;
  atRiskTime: string;
  rangeIncludesLondonToday: boolean;
}): string {
  if (!opts.rangeIncludesLondonToday) {
    return 'At risk only applies to London today — switch to Today before the cut-off';
  }
  if (opts.impactGbpMin != null) {
    return 'Modelled downgrade to economy (configurable rate)';
  }
  if (opts.consignments > 0) {
    return 'Counts ready — set downgrade rates in orders KPI config for £';
  }
  return `Nothing at risk against the ${opts.atRiskTime} promise today`;
}

export function ordersDailyStatusLabel(row: OrdersDailyRow): string {
  if (row.cancelled) return 'Cancelled';
  if (row.carrier_not_reported) return 'Not reported by carrier';
  if (row.direction === 'collection') {
    return row.collection_stage ? COLLECTION_STAGE_LABELS[row.collection_stage] : '—';
  }
  return DELIVERY_BUCKET_LABELS[row.status_bucket] ?? row.status_bucket;
}

/** Pallets on a partial POD, e.g. "9/13". Null when the leg is not part-delivered. */
export function partialDeliveryFraction(row: OrdersDailyRow): string | null {
  if (!row.partial_delivery) return null;
  const delivered = row.lifts_delivered;
  const total = row.lifts
    ?? (delivered != null && row.lifts_outstanding != null
      ? delivered + row.lifts_outstanding
      : null);
  if (delivered == null || total == null) return null;
  return `${formatLifts(delivered)}/${formatLifts(total)}`;
}

/** "9 delivered · 4 to go" for a caption / tooltip. Null when not part-delivered. */
export function partialDeliveryDetail(row: OrdersDailyRow): string | null {
  if (!row.partial_delivery || row.lifts_delivered == null || row.lifts_outstanding == null) {
    return null;
  }
  return `${formatLifts(row.lifts_delivered)} delivered · ${formatLifts(row.lifts_outstanding)} to go`;
}

const SOURCE_STATUS_TERMINAL = /^(completed|complete|job complete|delivered|closed)$/i;

/**
 * The source system's own status string when it claims the job is finished but
 * our evidence says it is still open — e.g. Palletways `"completed"` on a
 * consignment with pallets still to deliver. Null when there is no such
 * disagreement. Never used to change a figure, only to show the contradiction.
 */
export function sourceStatusContradiction(row: OrdersDailyRow): string | null {
  const status = row.source_status?.trim();
  if (!status || !row.open) return null;
  return SOURCE_STATUS_TERMINAL.test(status) ? status : null;
}

export type OrdersDecisionKind = 'partial' | 'regressed' | 'repeated_fail';

/**
 * A delivery leg a planner should actively decide about today — not just an
 * open drop, but one where something went wrong and it will not resolve
 * itself: pallets part-delivered with a balance, a POD that was undone, or a
 * consignment that has bounced off the van more than once.
 */
export function ordersDecisionKind(row: OrdersDailyRow): OrdersDecisionKind | null {
  if (row.direction !== 'delivery') return null;
  if (row.partial_delivery && ((row.lifts_outstanding ?? 0) > 0 || row.partial_ref_mismatch)) return 'partial';
  if (row.ever_delivered && !row.delivered) return 'regressed';
  if ((row.failed_attempts ?? 0) >= 2) return 'repeated_fail';
  return null;
}

/** One-line "why this needs a call" for the {@link ordersDecisionKind} strip. */
export function ordersDecisionReason(row: OrdersDailyRow): string {
  switch (ordersDecisionKind(row)) {
    case 'partial': {
      if (row.partial_ref_mismatch) return 'Part-delivered — pallet counts do not reconcile, check the POD';
      const frac = partialDeliveryFraction(row);
      const out = row.lifts_outstanding ?? 0;
      return `${frac ? `${frac} delivered — ` : ''}${formatLifts(out)} pallet${out === 1 ? '' : 's'} still to deliver`;
    }
    case 'regressed': {
      // `ever_delivered` only means "a POD landed, but the latest tracking scan
      // is not a delivery" — it does NOT tell us the leg went back to a depot.
      // Name the actual latest scan when we have it rather than assuming "depot".
      const label = statusCodeLabel(row.latest_status_code);
      return label
        ? `POD on record — latest scan is "${label}", not a delivery`
        : 'Had a POD, then a later scan re-opened the leg';
    }
    case 'repeated_fail':
      return `${row.failed_attempts} failed delivery runs — still at depot`;
    default:
      return 'Needs review';
  }
}

/**
 * Palletways tracking-note StatusCode → a short human label, for the journey
 * trail's per-note chip. Palletways-specific by nature (these are its codes);
 * an unknown code falls through to the raw string.
 */
const PALLETWAYS_STATUS_CODE_LABEL: Record<string, string> = {
  '0': 'Entered',
  '300': 'Scanned to trunk',
  '500': 'At hub',
  '550': 'Left hub',
  '700': 'At depot',
  '800': 'On delivery van',
  '900': 'Delivered (POD)',
};

export function statusCodeLabel(code: string | null | undefined): string | null {
  if (!code) return null;
  const key = String(code).trim();
  return PALLETWAYS_STATUS_CODE_LABEL[key] ?? key;
}

/**
 * The headline facts about a delivery leg's journey, from the fields the
 * projection already carries — shown above the note-by-note trail so a
 * planner sees the shape before reading the detail. Empty for an ordinary
 * clean delivery (nothing to flag).
 */
export function journeyTrailSummary(row: OrdersDailyRow): string[] {
  const out: string[] = [];
  const verb = row.direction === 'collection' ? 'collected' : 'delivered';

  if (row.direction === 'delivery') {
    const frac = partialDeliveryFraction(row);
    if (row.partial_delivery && frac) {
      const left = row.lifts_outstanding ?? 0;
      out.push(
        left > 0
          ? `Part-delivered — ${frac} pallets signed for, ${formatLifts(left)} still to deliver`
          : `Part-delivered — ${frac} pallets signed for`,
      );
    } else if (row.partial_delivery && row.partial_ref_mismatch) {
      out.push('Part-delivered — pallet counts do not reconcile, check the POD');
    }

    if (row.ever_delivered && !row.delivered) {
      const label = statusCodeLabel(row.latest_status_code);
      out.push(
        label
          ? `Proof of delivery on record, but the latest tracking scan ("${label}") is not a delivery — the leg still reads as open`
          : 'Proof of delivery on record, but a later scan re-opened the leg',
      );
    }

    const fails = row.failed_attempts ?? 0;
    if (fails > 0) {
      out.push(`${fails} failed delivery run${fails === 1 ? '' : 's'} — loaded onto a vehicle, returned with no POD`);
    }

    const contradiction = sourceStatusContradiction(row);
    if (contradiction) {
      out.push(`Source system reports "${contradiction}", but the leg is still open`);
    }
  }

  if (row.carrier_not_reported) {
    out.push(
      `Past its date and the hired haulier has not reported the ${
        row.direction === 'collection' ? 'collection' : 'delivery'
      } in the source system. Still open, not counted as ${verb}`,
    );
  }

  if (row.completion_source_discrepancy === 'inferred_from_status_text') {
    out.push(
      `Marked ${verb} from the source system's status ("${row.source_status ?? 'unknown'}") — no ${
        row.direction === 'collection' ? 'collection scan' : 'delivery scan or POD'
      } on record`,
    );
  } else if (row.completion_source_discrepancy === 'terminal_without_completion') {
    out.push(
      `Source system marks this closed ("${row.source_status ?? 'unknown'}") without a ${
        row.direction === 'collection' ? 'collection' : 'delivery'
      } — removed from the open backlog, not counted as ${verb}`,
    );
  }

  return out;
}

export function ordersDailyRowSearchText(row: OrdersDailyRow): string {
  return [
    row.consignment_no,
    row.tracking_id,
    row.booking_customer_label,
    row.customer,
    row.cust_ref,
    row.destination_postal_code,
    row.direction,
    row.type,
    row.col_depot,
    row.del_depot,
    row.service_code,
    row.service_name,
    row.due_date,
    row.due_time,
    ordersDailyStatusLabel(row),
    row.latest_status_code,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

/**
 * The booking customer as the drill-down shows it: the customer's name, with
 * its booking depot when another depot's account booked it. `undefined` when
 * customers were not read for this row (shown as "—"), never a guess.
 */
export function bookingCustomerText(row: OrdersDailyRow): string | undefined {
  if (!row.booking_customer_label) return undefined;
  return row.booking_customer_scope === 'network' && row.booking_depot
    ? `${row.booking_customer_label} (depot ${row.booking_depot})`
    : row.booking_customer_label;
}

/** Drill-down table column keys (sortable). */
export type OrdersDailySortField =
  | 'tracking_id'
  | 'consignment_no'
  | 'booking_customer'
  | 'customer'
  | 'cust_ref'
  | 'destination_postal_code'
  | 'direction'
  | 'type'
  | 'col_depot'
  | 'del_depot'
  | 'service'
  | 'due'
  | 'lifts'
  | 'status'
  | 'pod'
  | 'eta_on_time'
  | 'eta_at'
  | 'gps'
  | 'impact';

export type OrdersDailySortDirection = 'asc' | 'desc';

function cmpText(a: string, b: string): number {
  return a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });
}

function cmpNullableNumber(a: number | null | undefined, b: number | null | undefined): number {
  const aMissing = a == null || !Number.isFinite(a);
  const bMissing = b == null || !Number.isFinite(b);
  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;
  return (a as number) - (b as number);
}

function impactSortValue(row: OrdersDailyRow): number | null {
  if (row.missed_am && row.impact_gbp != null) return -Math.abs(row.impact_gbp);
  if (row.at_risk_1700) {
    if (row.impact_gbp_min != null) return row.impact_gbp_min;
    if (row.impact_gbp_max != null) return row.impact_gbp_max;
  }
  if (row.impact_gbp != null) return row.impact_gbp;
  return null;
}

function podSortValue(row: OrdersDailyRow): string {
  if (row.missed_am) return '0-missed';
  if (row.pod_time) return `1-${row.pod_date ?? ''}-${row.pod_time}`;
  if (row.pod_date) return `1-${row.pod_date}`;
  return '2-';
}

/** Late first when ascending (ops focus); missing ETA last. */
function etaOnTimeSortValue(row: OrdersDailyRow): number {
  if (row.eta_on_time === false) return 0;
  if (row.eta_on_time === true) return 1;
  return 2;
}

/** Stable compare for Orders daily drill-down sorting. */
export function compareOrdersDailyRows(
  a: OrdersDailyRow,
  b: OrdersDailyRow,
  field: OrdersDailySortField,
): number {
  switch (field) {
    case 'tracking_id':
      return cmpText(a.tracking_id || '', b.tracking_id || '');
    case 'consignment_no':
      return cmpText(a.consignment_no || a.tracking_id || '', b.consignment_no || b.tracking_id || '');
    case 'booking_customer':
      return cmpText(a.booking_customer_label || '', b.booking_customer_label || '');
    case 'customer':
      return cmpText(a.customer || '', b.customer || '');
    case 'cust_ref':
      return cmpText(a.cust_ref || '', b.cust_ref || '');
    case 'destination_postal_code':
      return cmpText(a.destination_postal_code || '', b.destination_postal_code || '');
    case 'direction':
      return cmpText(a.direction, b.direction);
    case 'type':
      return cmpText(a.type || '', b.type || '');
    case 'col_depot':
      return cmpText(a.col_depot || '', b.col_depot || '');
    case 'del_depot':
      return cmpText(a.del_depot || '', b.del_depot || '');
    case 'service':
      return cmpText(formatOrdersService(a), formatOrdersService(b));
    case 'due':
      return cmpText(`${a.due_date || ''}\0${a.due_time || ''}`, `${b.due_date || ''}\0${b.due_time || ''}`);
    case 'lifts':
      return cmpNullableNumber(a.lifts, b.lifts);
    case 'status':
      return cmpText(ordersDailyStatusLabel(a), ordersDailyStatusLabel(b));
    case 'pod':
      return cmpText(podSortValue(a), podSortValue(b));
    case 'eta_on_time':
      return etaOnTimeSortValue(a) - etaOnTimeSortValue(b);
    case 'eta_at':
      return cmpText(a.eta_at || '', b.eta_at || '');
    case 'gps':
      return gpsCheckSortValue(a) - gpsCheckSortValue(b);
    case 'impact':
      return cmpNullableNumber(impactSortValue(a), impactSortValue(b));
    default:
      return 0;
  }
}

export function sortOrdersDailyRows(
  rows: readonly OrdersDailyRow[],
  field: OrdersDailySortField,
  direction: OrdersDailySortDirection,
): OrdersDailyRow[] {
  const sorted = [...rows].sort((a, b) => {
    const cmp = compareOrdersDailyRows(a, b, field);
    if (cmp !== 0) return direction === 'asc' ? cmp : -cmp;
    // Stable tie-breakers so pagination does not shuffle.
    const tid = cmpText(a.tracking_id, b.tracking_id);
    if (tid !== 0) return tid;
    return cmpText(a.direction, b.direction);
  });
  return sorted;
}

/**
 * Why some trend days are gaps or out of date — one line for the chart
 * footer and CSV message. Null when every day in the window is current.
 * A missing day is never drawn as zero; this says why it is missing.
 */
export function ordersDailySeriesCoverageNote(
  unavailable: ReadonlyArray<{ date: string; reason: string }>,
  stale: ReadonlyArray<{ date: string }>,
): string | null {
  const days = (n: number) => `${n} day${n === 1 ? '' : 's'}`;
  const preparing = unavailable.filter((d) => d.reason === 'not_computed').length;
  const noFigures = unavailable.length - preparing;
  const parts: string[] = [];
  if (preparing > 0) parts.push(`${days(preparing)} still being prepared`);
  if (noFigures > 0) parts.push(`${days(noFigures)} with no stored figures`);
  if (parts.length > 0) parts[parts.length - 1] += ' (shown as gaps)';
  if (stale.length > 0) parts.push(`${days(stale.length)} out of date, refresh requested`);
  return parts.length > 0 ? `${parts.join('; ')}.` : null;
}
